import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";
import { contactV1Or2, paths, routes } from "config/paths";
import RouterLink from "next/link";

import { MangoLogo } from "~mangomap-ui/templates/onboarding-template/fixtures/logo";
import type { NavItemConfig } from "~ui-components/types/nav";

import { AiSummaryLinks } from "./ai-summary-links";

interface GroupConfig {
	key: string;
	title: string;
	items: NavItemConfig[];
}

const groups = [
	{
		key: "solutions",
		title: "Solutions",
		items: [
			{ key: "gis", title: "Mango for GIS", href: "/" },
			{
				key: "franchise",
				title: "Mango for Franchising",
				href: routes.franchise,
			},
			{
				key: "government",
				title: "Mango for Government",
				href: paths.solutionsGovernment,
			},
			{
				key: "environment",
				title: "Mango for Environment",
				href: paths.solutionsEnvironment,
			},
			{ key: "partners", title: "Partner Program", href: paths.partners },
		],
	},
	{
		key: "product",
		title: "Product",
		items: [
			{ key: "case-studies", title: "Case Studies", href: "/case-studies" },
			{
				key: "gis-guide",
				title: "Beginner's Guide to GIS",
				href: "/beginners-guide-to-gis",
			},
			{
				key: "documentation",
				title: "Documentation",
				external: true,
				href: "https://help.mangomap.com",
			},
			{ key: "changelog", title: "Changelog", href: paths.changelog },
			{
				key: "status",
				title: "Status",
				href: "https://uptime.com/s/mangomap",
				external: true,
			},
		],
	},
	{
		key: "compare",
		title: "Compare",
		items: [
			{
				key: "vs-arcgis-online",
				title: "ArcGIS Online",
				href: "/alternative-to/arcgis-online",
			},
			{ key: "vs-mapbox", title: "Mapbox", href: "/alternative-to/mapbox" },
			{ key: "vs-felt", title: "Felt", href: "/alternative-to/felt" },
			{ key: "vs-atlas-co", title: "Atlas", href: "/alternative-to/atlas-co" },
			{
				key: "all-comparisons",
				title: "See all comparisons",
				href: "/alternative-to",
			},
		],
	},
	{
		key: "company",
		title: "Company",
		items: [
			{ key: "about", title: "About Mango", href: "/about-us" },
			{ key: "contact", title: "Contact", href: contactV1Or2 },
			{
				key: "linkedin",
				title: "LinkedIn",
				href: "https://www.linkedin.com/company/mangomap",
				external: true,
			},
			{
				key: "yt",
				title: "YouTube",
				href: "https://www.youtube.com/@MangoMap",
				external: true,
			},
		],
	},
] satisfies GroupConfig[];

export function Footer() {
	return (
		<Box
			component="footer"
			sx={{
				bgcolor: "var(--mui-palette-background-default)",
				borderTop: "1px solid var(--mui-palette-divider)",
				pb: 6,
				pt: { md: 15, xs: 6 },
			}}>
			<Container maxWidth="lg">
				<Grid
					container
					spacing={3}>
					<Grid
						sx={{ order: { xs: groups.length + 1, md: 1 } }}
						size={{
							md: 3,
							sm: 4,
							xs: 12,
						}}>
						<Stack
							spacing={2.5}
							sx={{ alignItems: "flex-start" }}>
							<MangoLogo />
							<AiSummaryLinks />
						</Stack>
					</Grid>
					{groups.map((section, index) => (
						<Grid
							key={section.key}
							sx={{ order: { md: index + 2, xs: index + 1 } }}
							size={{
								md: "grow",
								sm: 6,
								xs: 12,
							}}>
							<Typography
								color="text.secondary"
								variant="overline">
								{section.title}
							</Typography>
							<Stack
								// component="p"
								spacing={1}
								sx={{ m: 0, p: 0 }}>
								{section.items.map((item) => (
									<NavItem
										{...item}
										key={item.key}
									/>
								))}
							</Stack>
						</Grid>
					))}
				</Grid>

				<Divider sx={{ my: 6 }} />
				<Stack
					direction="row"
					spacing={3}
					sx={{ flexWrap: "wrap", rowGap: 1 }}>
					<Link
						color="text.secondary"
						href="/terms"
						variant="caption">
						Terms of Service
					</Link>
					<Link
						color="text.secondary"
						href="/privacy"
						variant="caption">
						Privacy Policy
					</Link>
					<Link
						color="text.secondary"
						href="/security"
						variant="caption">
						Data Security
					</Link>
					<Link
						color="text.secondary"
						component="a"
						href="//app-assets/agents-version.md"
						rel="noopener noreferrer"
						target="_blank"
						variant="caption">
						Agent version &#x1F916;
					</Link>
				</Stack>
				<Typography
					color="text.secondary"
					variant="caption">
					© {new Date().getFullYear()} Mangomap LIMITED. 1 Victoria Street,
					Bristol BS1 6AA, UK.{" "}
				</Typography>
				<Typography
					color="text.secondary"
					variant="caption">
					We use cookies to keep MangoMap running, improve performance and
					security, and measure our marketing.
				</Typography>
			</Container>
		</Box>
	);
}

function NavItem({ href, external, title }: NavItemConfig) {
	return (
		<Stack
			direction="row"
			key={title}
			spacing={1}
			sx={{ alignItems: "center" }}>
			<Link
				{...(href
					? external
						? { component: "a", href, target: "_blank" }
						: { component: RouterLink, href }
					: {})}
				color="text.secondary"
				variant="caption">
				{title}
			</Link>
		</Stack>
	);
}
